import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthCard from './components/AuthCard';
import { verifyEmail } from './hooks/useAuth';

type VerifyStatus = 'verifying' | 'success' | 'error';

export default function EmailVerificationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [error, setError] = useState('');

  const timeoutRef = useRef<number | null>(null);
  const requestRef = useRef<{ key: string; promise: Promise<any> } | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const key = `${email}|${token}`;

    if (!email || !token) {
      setStatus('error');
      setError('The email or token are invalid.');
      return;
    }

    let cancelled = false;

    setStatus('verifying');
    setError('');

    const promise = requestRef.current?.key === key
      ? requestRef.current.promise
      : verifyEmail(email, token);

    if (!requestRef.current || requestRef.current.key !== key) {
      requestRef.current = { key, promise };
    }

    (async () => {
      try {
        await promise;
        if (cancelled) return;

        setStatus('success');
        timeoutRef.current = window.setTimeout(() => {
          navigate('/login', { replace: true });
        }, 500);
      } catch {
        if (cancelled) return;
        setStatus('error');
        setError('The email or token are invalid.');
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [email, token, navigate]);

  return (
    <AuthCard
      pageTestId="page-verify-email"
      cardTestId="verify-email-card"
      ariaLabel="Verify email"
      title="Retail ID"
      subtitle="Email verification"
    >
      {status === 'verifying' && (
        <div
          className="alert"
          role="status"
          data-testid="verify-email-verifying"
          style={{
            background: '#f6f7f9',
            border: '1px solid #d5d8dc',
            color: '#1f2a32',
          }}
        >
          Verifying your email…
        </div>
      )}

      {status === 'success' && (
        <div
          className="alert"
          role="status"
          data-testid="verify-email-success"
          style={{
            background: '#f0fff4',
            border: '1px solid #b6f2c2',
            color: '#1f7a3a',
          }}
        >
          Email verified. Redirecting to sign in…
        </div>
      )}

      {status === 'error' && (
        <>
          <div className="alert error" role="alert" data-testid="verify-email-error">
            {error || 'The email or token are invalid.'}
          </div>
          <button
            type="button"
            className="button cta"
            style={{ width: '100%' }}
            onClick={() => navigate('/login')}
            data-testid="verify-email-to-login"
          >
            Go to Sign In
          </button>
        </>
      )}
    </AuthCard>
  );
}
